import type { ContactDetails } from "@/types/contact";
import type { ContactSummary } from "@/types/entities";

export function contactPrimaryLabel(
  c: Pick<ContactSummary, "name" | "contactPerson" | "email" | "externalId">,
): string {
  return (
    (c.name && c.name.trim()) ||
    (c.contactPerson && String(c.contactPerson).trim()) ||
    (c.email && c.email.trim()) ||
    `(Contact ${c.externalId || "—"})`
  );
}

export function contactSelectOption(c: ContactSummary): { value: string; label: string } {
  const bits = [c.town, c.postcode].filter(Boolean);
  const suffix = bits.length ? ` — ${bits.join(" · ")}` : "";
  return { value: c.id, label: `${contactPrimaryLabel(c)}${suffix}` };
}

/** Map GET /contacts/:id into summary fields used by owner dropdowns. */
export function contactDetailsToSummary(raw: ContactDetails, fallbackId?: string): ContactSummary {
  const primary = raw.person?.[0];
  const nameFromPerson = primary
    ? `${primary.forename ?? ""} ${primary.surname ?? ""}`.trim()
    : "";
  const name = (raw.name && raw.name.trim()) || nameFromPerson || null;
  const contactPerson =
    (raw as ContactDetails & { contactPerson?: string | null }).contactPerson?.trim() ||
    nameFromPerson ||
    null;

  return {
    id: raw.id || fallbackId || "",
    sourceCrm: raw.sourceCrm,
    externalId: raw.externalId ?? "",
    name,
    forename: primary?.forename?.trim() || null,
    surname: primary?.surname?.trim() || null,
    email: raw.email ?? null,
    mobilePhone: raw.mobilePhone ?? null,
    town: raw.town ?? null,
    postcode: raw.postcode ?? null,
    category: typeof raw.category === "string" ? raw.category : null,
    externalBranchId:
      typeof raw.externalBranchId === "string"
        ? raw.externalBranchId
        : Array.isArray(raw.externalBranchId)
          ? raw.externalBranchId[0] ?? null
          : null,
    archived: raw.archived ?? null,
    atlasModifiedAt: raw.atlasModifiedAt ?? null,
    contactPerson,
  };
}

/** Owner dropdown options: always include selected IDs; optionally client-filter the rest. */
export function buildOwnerSelectOptions(
  contactsPool: ContactSummary[],
  ownerContactIds: string[],
  contactSearchQuery?: string,
): { value: string; label: string }[] {
  const selectedSet = new Set(ownerContactIds.map((id) => id.trim()).filter(Boolean));
  const q = (contactSearchQuery ?? "").trim().toLowerCase();

  const visiblePool = contactsPool.filter((c) => {
    if (selectedSet.has(c.id)) return true;
    if (!q) return true;
    const primary = contactPrimaryLabel(c);
    const bits = [c.category, c.town, c.postcode].filter(Boolean);
    const haystack = [primary, ...bits, c.externalId].join(" ").toLowerCase();
    return haystack.includes(q);
  });

  const optionMap = new Map<string, { value: string; label: string }>();
  for (const c of visiblePool) {
    optionMap.set(c.id, contactSelectOption(c));
  }
  for (const id of selectedSet) {
    if (!optionMap.has(id)) {
      optionMap.set(id, { value: id, label: "Loading contact…" });
    }
  }
  return [...optionMap.values()];
}
